import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { authApi } from "../services/api";
import Logo from "../components/Logo";
import Button from "../components/Button";
import Input from "../components/Input";
import StepBar from "../components/StepBar";
import Callout from "../components/Callout";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { OPERATORS } from "../constants";

const T = { night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3", gold:"#C9922A" };

/**
 * Page de verification du compte apres inscription (GP-02 Etape 2).
 * Le code OTP est envoye a la fois par SMS et par email a l'adresse
 * fournie a l'inscription.
 */
export default function VerifyOtp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { phoneNumber, email } = location.state || {};
  const [resending, setResending] = useState(false);

  const [step, setStep] = useState(0); // 0 = code, 1 = mobile money
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [mobileMoneyNumber, setMobileMoneyNumber] = useState("");
  const [mobileMoneyOperator, setMobileMoneyOperator] = useState("");
  const otpRefs = Array(6).fill(null).map(() => useRef());

  useEffect(() => {
    if (!phoneNumber) navigate("/register", { replace: true });
  }, [phoneNumber, navigate]);

  const handleOtp = (i, v) => {
    const n = [...otp]; n[i] = v.replace(/\D/g, "").slice(-1); setOtp(n);
    if (v && i < 5) otpRefs[i + 1].current?.focus();
  };

  const submitOtp = async ev => {
    ev.preventDefault();
    if (otp.some(v => !v)) { toast.error(t("verify_otp.incomplete")); return; }
    setLoading(true);
    try {
      await authApi.verifyOtp({ phoneNumber, otpCode: otp.join("") });
      toast.success(t("verify_otp.verified_toast"));
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.message || t("verify_otp.error"));
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (resending) return;
    setResending(true);
    try {
      const res = await authApi.resendOtp({ phoneNumber });
      toast.success(res.data?.message || t("verify_otp.resend_done"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("verify_otp.error"));
    } finally { setResending(false); }
  };

  const finish = () => {
    toast.success(t("register.finish_toast"));
    navigate("/login");
  };

  if (!phoneNumber) return null;

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center",
      justifyContent:"center", background:"#F7F9FC",
      padding:"32px 24px", fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ width:"100%", maxWidth:"480px" }}>
        <div style={{ marginBottom:"32px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <Logo size="md" />
            <LanguageSwitcher />
          </div>
          <h1 style={{ fontSize:"26px", fontWeight:800, color:T.night, marginTop:"24px", marginBottom:"4px" }}>
            {t("verify_otp.title")}</h1>
          <p style={{ color:"#64748B", fontSize:"13px", marginBottom:"20px" }}>
            {step === 0 ? t("verify_otp.subtitle_code") : t("verify_otp.subtitle_money")}</p>
          <StepBar total={2} current={step} />
        </div>

        {/* Etape 0 - Code OTP */}
        {step === 0 && (
          <form onSubmit={submitOtp}>
            <Callout variant="cyan" icon="📧">
              {email
                ? <>{t("verify_otp.code_sent")} <strong>{phoneNumber}</strong> {t("verify_otp.code_sent_email")} <strong>{email}</strong>. </>
                : <>{t("verify_otp.code_sent_account_email")} </>}
              {t("verify_otp.code_valid")}
            </Callout>
            <p style={{ textAlign:"center", fontSize:"13px", fontWeight:600,
              color:"#334155", marginBottom:"16px" }}>{t("verify_otp.code_label")}</p>
            <div style={{ display:"flex", gap:"8px", justifyContent:"center", marginBottom:"24px" }}>
              {otp.map((v, i) => (
                <input key={i} ref={otpRefs[i]} value={v} inputMode="numeric"
                  onChange={e => handleOtp(i, e.target.value)}
                  onKeyDown={e => { if (e.key === "Backspace" && !v && i > 0) otpRefs[i - 1].current?.focus(); }}
                  maxLength={1}
                  style={{ width:"52px", height:"60px", textAlign:"center",
                    fontSize:"26px", fontWeight:700, fontFamily:"'DM Sans',sans-serif",
                    border:`2.5px solid ${v ? T.cyan : "#E2E8F0"}`,
                    borderRadius:"12px", color:T.night, background:"#fff", outline:"none",
                    transition:"border-color 0.15s" }} />
              ))}
            </div>
            <div style={{ textAlign:"center", marginBottom:"20px" }}>
              <span style={{ fontSize:"13px", color:"#94A3B8" }}>{t("verify_otp.resend_note")} </span>
              <span role="button" tabIndex={0} onClick={resend}
                onKeyDown={e => { if (e.key === "Enter") resend(); }}
                style={{ fontSize:"13px", color:T.cyan, fontWeight:600,
                  cursor: resending ? "wait" : "pointer", opacity: resending ? 0.6 : 1 }}>
                {t("verify_otp.resend")}</span>
            </div>
            <Button type="submit" variant="cyan" full loading={loading}
              disabled={otp.some(v => !v)}>{t("verify_otp.verify")}</Button>
            <p style={{ textAlign:"center", marginTop:"16px", fontSize:"13px", color:"#64748B" }}>
              <Link to="/register" style={{ color:T.cyan, fontWeight:600, textDecoration:"none" }}>
                {t("verify_otp.back_to_register")}</Link></p>
          </form>
        )}

        {/* Etape 1 - Mobile Money */}
        {step === 1 && (
          <div>
            <Callout variant="gold" icon="✅">
              {t("register.verified_callout")}
            </Callout>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px", marginBottom:"16px" }}>
              {OPERATORS.map(op => (
                <div key={op.id} onClick={() => setMobileMoneyOperator(op.id)}
                  style={{
                    border: `2px solid ${mobileMoneyOperator === op.id ? op.color : "#E2E8F0"}`,
                    borderRadius:"14px", padding:"16px 12px", textAlign:"center",
                    cursor:"pointer", background: mobileMoneyOperator === op.id ? op.bg : "#fff",
                    transition:"all 0.15s",
                  }}>
                  <div style={{ fontSize:"28px" }}>{op.emoji}</div>
                  <div style={{ fontSize:"12px", fontWeight:700, color:"#1E293B", marginTop:"6px" }}>
                    {op.label}</div>
                </div>
              ))}
            </div>
            <Input label={t("register.mobile_money")} name="mobileMoneyNumber" type="tel"
              placeholder="+237 6 XX XX XX XX" value={mobileMoneyNumber}
              onChange={e => setMobileMoneyNumber(e.target.value)} optional
              note={t("register.mobile_money_note")} />
            <div style={{ display:"flex", gap:"12px" }}>
              <Button variant="ghost" onClick={finish}>{t("register.skip")}</Button>
              <Button variant="gold" full onClick={finish}>{t("register.finish")}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
